import { useCallback, useContext, useMemo } from "react";

import { useQueryClient } from "@tanstack/react-query";

import {
  CREATE_SOURCE_FAILED_TO_CREATE_ERROR_HEADER,
  CREATE_SOURCE_PERMISSION_DENIED_HEADER,
  CREATE_SOURCE_PERMISSION_DENIED_SUBHEADER,
  CREATE_SOURCE_UNEXPECTED_ERROR,
} from "@/pages/sources/components/create/sources/constants";
import {
  getCreateSourceNameError,
  isCreateSourceValueProvided,
} from "@/pages/sources/components/create/sources/utils";
import { CreateSourceWikipediaActionType } from "@/pages/sources/components/create/sources/wikipedia/actions";
import {
  CreateSourceWikipediaContext,
  CreateSourceWikipediaContextShape,
} from "@/pages/sources/components/create/sources/wikipedia/CreateSourceWikipediaProvider";
import { CreateSourceWikipediaPhase } from "@/pages/sources/components/create/sources/wikipedia/types";
import {
  buildCreateSourceWikipediaRequest,
  getCreateSourceWikipediaSeedUrlError,
  isValidWikipediaUrl,
} from "@/pages/sources/components/create/sources/wikipedia/utils";

import { ToastVariant } from "@/providers/toast/Toast";
import { useToast } from "@/providers/toast/useToast";

import { useCreateSourceMutation } from "@/api/mutations/sources";
import { createListSourcesInfiniteQueryKey } from "@/api/queries/sources";
import { isPermissionError } from "@/api/utils";

export function useCreateSourceWikipediaContext(): CreateSourceWikipediaContextShape {
  const context = useContext(CreateSourceWikipediaContext);
  if (!context) {
    throw new Error(
      "useCreateSourceWikipediaContext must be used within a CreateSourceWikipediaProvider",
    );
  }
  return context;
}

interface UseCreateSourceWikipediaOptions {
  onSuccess?: (sourceId: string) => void;
}

export function useCreateSourceWikipedia({ onSuccess }: UseCreateSourceWikipediaOptions) {
  const { state, dispatch } = useCreateSourceWikipediaContext();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { mutateAsync: createSourceMutation } = useCreateSourceMutation();

  const nameError = useMemo(() => getCreateSourceNameError(state), [state]);
  const seedUrlError = useMemo(() => getCreateSourceWikipediaSeedUrlError(state), [state]);

  const isDisabled =
    state.phase === CreateSourceWikipediaPhase.IN_PROGRESS ||
    state.phase === CreateSourceWikipediaPhase.COMPLETED;

  const isValid = useMemo(() => {
    return (
      isCreateSourceValueProvided(state.sourceName) &&
      isCreateSourceValueProvided(state.seedUrl) &&
      isValidWikipediaUrl(state.seedUrl)
    );
  }, [state.sourceName, state.seedUrl]);

  const save = useCallback(async () => {
    dispatch({ type: CreateSourceWikipediaActionType.SET_SHOULD_SHOW_ERRORS, payload: true });

    if (
      state.phase === CreateSourceWikipediaPhase.IN_PROGRESS ||
      !isCreateSourceValueProvided(state.sourceName) ||
      !isValidWikipediaUrl(state.seedUrl)
    ) {
      return;
    }

    try {
      dispatch({
        type: CreateSourceWikipediaActionType.SET_PHASE,
        payload: CreateSourceWikipediaPhase.IN_PROGRESS,
      });

      const createRequest = buildCreateSourceWikipediaRequest(
        state.sourceName,
        state.seedUrl,
        state.linksPerPage,
        state.crawlDepth,
        state.maxPages,
      );
      const response = await createSourceMutation(createRequest);
      const sourceId = response.source?.id;

      if (!sourceId) {
        throw new Error("Create source response did not include a source id");
      }

      dispatch({
        type: CreateSourceWikipediaActionType.SET_PHASE,
        payload: CreateSourceWikipediaPhase.COMPLETED,
      });

      void queryClient.invalidateQueries({ queryKey: createListSourcesInfiniteQueryKey() });

      showToast({
        variant: ToastVariant.SUCCESS,
        header: "Source created",
        subheader: "The source has been created successfully",
      });

      if (onSuccess) onSuccess(sourceId);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : CREATE_SOURCE_UNEXPECTED_ERROR;
      dispatch({
        type: CreateSourceWikipediaActionType.SET_PHASE,
        payload: CreateSourceWikipediaPhase.ERROR,
      });
      dispatch({ type: CreateSourceWikipediaActionType.SET_ERROR, payload: errorMessage });
      const isDenied = isPermissionError(error);
      showToast({
        variant: ToastVariant.ERROR,
        header: isDenied
          ? CREATE_SOURCE_PERMISSION_DENIED_HEADER
          : CREATE_SOURCE_FAILED_TO_CREATE_ERROR_HEADER,
        subheader: isDenied ? CREATE_SOURCE_PERMISSION_DENIED_SUBHEADER : errorMessage,
      });
    }
  }, [
    state.phase,
    state.sourceName,
    state.seedUrl,
    state.linksPerPage,
    state.crawlDepth,
    state.maxPages,
    dispatch,
    createSourceMutation,
    queryClient,
    showToast,
    onSuccess,
  ]);

  return {
    state,
    dispatch,
    save,
    isDisabled,
    isValid,
    nameError,
    seedUrlError,
  };
}
